import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./buyCreditsTab.module.css";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { isAuthSelector } from "../../../../services/selectors/auth.selectors";
import { useGetAllCurrenciesQuery } from "../../../../services/api/utils.api";
import {
  CreditType,
  FeatureCreditPackageDto,
  SeatSubscriptionDto,
  useListFeatureCreditPackagesQuery,
  useListSeatSubscriptionsQuery,
} from "../../../../services/api/featureCreditPackagesApi";
import { useLazyGetUsersByCompanyIdWithRecommendsQuery } from "../../../../services/api/user.api";
import {
  creditTypeLabel,
  packageSizeLabel,
  purchaseAmountLabel,
} from "../../../../utils/featureCreditPackages";
import { seatPackState, sortSeatPacks } from "../../../../utils/seatSubscriptions";
import { isStaffRole, STAFF_ROLE_IDS } from "../../../../utils/featureCreditsAuth";
import { TLabeledItem } from "../../../../types/types";
import { formatDateInIsrael, packageFeatureLabel } from "../../../../utils/utils";
import { PurchaseOutcome, useFeatureCreditPurchase } from "./useFeatureCreditPurchase";

const CREDIT_TYPES: CreditType[] = ["token", "count", "seat"];

interface BuyCreditsTabProps {
  /** Lets the no_saved_card CTA send the buyer to the tab that can actually update the card. */
  onNavigateToTab?: (tabId: string) => void;
}

interface PurchaseOutcomeBannerProps {
  outcome: PurchaseOutcome;
  beneficiaryLabel: string;
  onDismiss: () => void;
  onUpdatePaymentMethod: () => void;
  onViewSeatPacks: () => void;
}

function PurchaseOutcomeBanner({
  outcome,
  beneficiaryLabel,
  onDismiss,
  onUpdatePaymentMethod,
  onViewSeatPacks,
}: PurchaseOutcomeBannerProps) {
  const variantClass =
    outcome.kind === "success"
      ? styles.statusBannerSuccess
      : outcome.kind === "declined" || outcome.kind === "reconciliation" || outcome.kind === "error"
        ? styles.statusBannerError
        : "";

  const message = (() => {
    switch (outcome.kind) {
      case "success":
        // Token packs report creditsGranted and seat/count packs amountGranted, so the size has to
        // come from the same split the purchase-history rows use — reading either field alone shows
        // "0" for half the pack types.
        return `${purchaseAmountLabel(outcome.response)} ${t("PURCHASE_SUCCESS_GRANTED_TO")} ${beneficiaryLabel}`;
      case "declined":
        return t("BUY_CARD_DECLINED_ERROR");
      case "no_saved_card":
        return t("BUY_NO_SAVED_CARD_ERROR");
      case "reconciliation":
        return outcome.variant === "captured_not_credited"
          ? t("BUY_CAPTURED_NOT_CREDITED_ERROR")
          : t("BUY_STILL_CONFIRMING_ERROR");
      case "throttled":
        return t("BUY_THROTTLED_ERROR");
      case "stale":
        return t("BUY_STALE_ELIGIBILITY_ERROR");
      case "error":
        return outcome.message;
    }
  })();

  const purchaseId = outcome.kind === "reconciliation" ? outcome.purchaseId : undefined;

  return (
    <div className={`${styles.statusBanner} ${variantClass}`}>
      <span>
        {message}
        {purchaseId != null && ` — ${t("PURCHASE_REFERENCE_LABEL")} #${purchaseId}`}
      </span>
      {outcome.kind === "no_saved_card" && (
        <button type="button" className={styles.bannerActionButton} onClick={onUpdatePaymentMethod}>
          {t("UPDATE_CARD")}
        </button>
      )}
      {outcome.kind === "success" && outcome.response.subscriptionId != null && (
        <button type="button" className={styles.bannerActionButton} onClick={onViewSeatPacks}>
          {t("GO_TO_SEAT_PACKS")}
        </button>
      )}
      <button
        type="button"
        className={styles.statusBannerDismiss}
        aria-label={t("DISMISS")}
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

interface EligibilityBlockedMessageProps {
  reason: string | null;
  onNavigateToTab?: (tabId: string) => void;
}

function EligibilityBlockedMessage({ reason, onNavigateToTab }: EligibilityBlockedMessageProps) {
  const navigate = useNavigate();

  if (reason === "company_missing_billing_email") {
    return (
      <div className={styles.eligibilityBlocked}>
        <p>{t("MISSING_BILLING_EMAIL_MESSAGE")}</p>
        {/* The company email this gate reads is edited in the identity section, not the billing one. */}
        <button
          type="button"
          className={styles.buyButton}
          onClick={() => navigate("/companySettings?section=identity")}
        >
          {t("GO_TO_COMPANY_SETTINGS")}
        </button>
      </div>
    );
  }

  // Default to the no_active_subscription framing — the only other documented reason.
  return (
    <div className={styles.eligibilityBlocked}>
      <p>{t("BUY_NO_ACTIVE_SUBSCRIPTION_MESSAGE")}</p>
      {onNavigateToTab && (
        <button
          type="button"
          className={styles.buyButton}
          onClick={() => onNavigateToTab("subscription")}
        >
          {t("GO_TO_SUBSCRIPTION")}
        </button>
      )}
    </div>
  );
}

type ScopeType = "company" | "specific";

interface ScopeSelectorProps {
  packageCreditType: CreditType;
  selectedScope: ScopeType | null;
  selectedUserId: number | null;
  userError: boolean;
  staffMembers: TLabeledItem[];
  disabled: boolean;
  onScopeChange: (scope: ScopeType) => void;
  onUserChange: (userId: number | null) => void;
}

function ScopeSelector({
  packageCreditType,
  selectedScope,
  selectedUserId,
  userError,
  staffMembers,
  disabled,
  onScopeChange,
  onUserChange,
}: ScopeSelectorProps) {
  const isSeat = packageCreditType === "seat";

  if (isSeat) {
    return (
      <div className={styles.scopeSection}>
        <label className={styles.scopeLabel}>{t("SCOPE_TYPE")}</label>
        <div className={styles.scopeDisabled}>
          {t("SCOPE_WHOLE_COMPANY")}
        </div>
      </div>
    );
  }

  const scopeOptions: TLabeledItem[] = [
    // { value: "self", label: t("SCOPE_MYSELF") }, scope self was removed (Inon decision)
    { value: "company", label: t("SCOPE_WHOLE_COMPANY") },
    { value: "specific", label: t("SCOPE_SPECIFIC_STAFF_MEMBER") },
  ];

  const selectedScopeOption = selectedScope
    ? scopeOptions.find((o) => o.value === selectedScope) ?? null
    : null;

  const selectedUserOption = selectedUserId
    ? staffMembers.find((o) => o.value === selectedUserId.toString()) ?? null
    : null;

  return (
    <div className={styles.scopeSection}>
      <label htmlFor="scope" className={styles.scopeLabel}>
        {t("SCOPE_TYPE")}
      </label>
      <Select
        inputId="scope"
        options={scopeOptions}
        value={selectedScopeOption}
        isDisabled={disabled}
        onChange={(newValue: SingleValue<TLabeledItem>) => {
          const scope = newValue?.value as ScopeType | undefined;
          if (scope) {
            onScopeChange(scope);
            if (scope !== "specific") onUserChange(null);
          }
        }}
        placeholder={t("SELECT_SCOPE")}
        className={styles.scopeSelect}
        menuPosition="fixed"
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />

      {selectedScope === "specific" && (
        <div className={styles.userSection}>
          <label htmlFor="targetUser" className={styles.scopeLabel}>
            {t("SELECT_STAFF_MEMBER")}
          </label>
          <Select
            inputId="targetUser"
            options={staffMembers}
            value={selectedUserOption}
            isDisabled={disabled}
            onChange={(newValue: SingleValue<TLabeledItem>) => {
              onUserChange(newValue ? Number(newValue.value) : null);
            }}
            placeholder={t("SELECT_STAFF_MEMBER")}
            className={styles.scopeSelect}
            isClearable
            menuPosition="fixed"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          {userError && (
            <span className={styles.userError}>{t("SELECT_USER_ERROR")}</span>
          )}
        </div>
      )}
    </div>
  );
}

interface PackageCardProps {
  pkg: FeatureCreditPackageDto;
  isSelected: boolean;
  disabled: boolean;
  currencyLabel: (currencyId: number) => string;
  onSelectPackage: (pkg: FeatureCreditPackageDto) => void;
}

function PackageCard({
  pkg,
  isSelected,
  disabled,
  currencyLabel,
  onSelectPackage,
}: PackageCardProps) {
  // No currency to pick: the buy screen asks for billingCurrencyOnly packs, so every price row
  // left on a pack is already the company's billing currency, and the `purchasable` filter drops
  // the packs left with none. Taking the first row shows — and charges — one consistent price even
  // in the one case that leaves two, two Currency rows sharing a code.
  const price = pkg.prices[0];

  return (
    <div className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}>
      <button
        type="button"
        className={styles.cardMain}
        disabled={disabled}
        onClick={() => onSelectPackage(pkg)}
      >
        <span className={styles.cardFeature}>{packageFeatureLabel(pkg)}</span>
        <span className={styles.cardName}>
          {pkg.name}
          <span className={styles.cardNameEn}>{pkg.nameEn}</span>
        </span>
        <span className={styles.cardAmount}>{packageSizeLabel(pkg)}</span>
        {pkg.description && <span className={styles.cardDescription}>{pkg.description}</span>}
      </button>

      <div className={styles.cardPriceRow}>
        <span className={styles.cardPrice}>
          {currencyLabel(price.currencyId)} {price.priceAfterTax}
        </span>
        {pkg.creditType === "seat" && (
          <span className={styles.recurringBadge}>{t("RECURRING_BADGE")}</span>
        )}
      </div>
    </div>
  );
}

interface ExistingSeatPackNoticeProps {
  pkg: FeatureCreditPackageDto;
  seatRows: SeatSubscriptionDto[];
  onNavigateToTab?: (tabId: string) => void;
}

// Advisory only — a second concurrent seat pack is a legitimate purchase, so this never blocks
// Confirm. It only makes sure the admin has seen what the company already holds for this same
// feature before adding to it.
function ExistingSeatPackNotice({ pkg, seatRows, onNavigateToTab }: ExistingSeatPackNoticeProps) {
  const existing = seatRows.filter((row) => row.featureKey === pkg.featureKey);
  if (existing.length === 0) return null;

  const seats = existing.reduce((total, row) => total + row.seats, 0);
  // Most urgent row drives the status clause — same ordering the Seat Packs tab uses (past_due
  // first), so a past-due pack is never buried behind an unrelated renewing one in the aggregate.
  const primary = sortSeatPacks(existing)[0];
  const state = seatPackState(primary);

  const status = (() => {
    switch (state.kind) {
      case "past_due":
        return t("BUY_EXISTING_SEAT_PACK_STATUS_PAST_DUE");
      case "ending":
        return state.endsOn
          ? t("BUY_EXISTING_SEAT_PACK_STATUS_ENDING", { date: formatDateInIsrael(state.endsOn) })
          : t("BUY_EXISTING_SEAT_PACK_STATUS_ENDING_IMMINENT");
      case "renewing":
        return t("BUY_EXISTING_SEAT_PACK_STATUS_RENEWING", { date: formatDateInIsrael(state.nextChargeOn) });
    }
  })();

  return (
    <p className={styles.existingPackNotice}>
      <span>
        {t("BUY_EXISTING_SEAT_PACK_NOTICE_PREFIX", { seats, resource: packageFeatureLabel(pkg) })}
        {" ("}
        {status}
        {"). "}
        {t("BUY_EXISTING_SEAT_PACK_NOTICE_SUFFIX", { newSeats: pkg.amount ?? 0 })}
      </span>
      {onNavigateToTab && (
        <button
          type="button"
          className={styles.existingPackLink}
          onClick={() => onNavigateToTab("seat-packs")}
        >
          {t("GO_TO_SEAT_PACKS")}
        </button>
      )}
    </p>
  );
}

export const BuyCreditsTab: React.FC<BuyCreditsTabProps> = ({ onNavigateToTab }) => {
  const admin = useAppSelector(adminSelector);
  const isAuth = useAppSelector(isAuthSelector);
  const { data: currencies = [] } = useGetAllCurrenciesQuery();
  // billingCurrencyOnly: true — the backend narrows every pack's prices to the company's own
  // billing currency (the only one Buy will accept), so a pack with none of that currency's price
  // rows arrives with prices: [] and is dropped by the `purchasable` filter below, and no pack here
  // can ever offer a currency picker for a currency Buy would refuse with currency_mismatch.
  const { data: packages = [], isLoading, isError } = useListFeatureCreditPackagesQuery({
    billingCurrencyOnly: true,
  });
  const [getStaffMembers, { data: staffResponse }] = useLazyGetUsersByCompanyIdWithRecommendsQuery();
  // Same cache entry the past-due banner and the Seat Packs tab already read — reused here rather
  // than firing a second request for the same data.
  const { data: seatRows = [] } = useListSeatSubscriptionsQuery();

  const purchase = useFeatureCreditPurchase(isAuth && !!admin.id);

  const [selectedPackage, setSelectedPackage] = useState<FeatureCreditPackageDto | null>(null);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | null>(null);
  const [selectedScope, setSelectedScope] = useState<ScopeType | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userError, setUserError] = useState(false);
  const [lastBeneficiaryLabel, setLastBeneficiaryLabel] = useState("");

  const staffMembers = useMemo(
    () =>
      (staffResponse?.Users ?? [])
        .filter((u) => isStaffRole(u.Users.RoleId) && Number(u.Users.Id) !== Number(admin.id))
        .map((u) => {
          const name = u.Users.FullName || `${u.Users.FirstName} ${u.Users.LastName}`;
          return { value: u.Users.Id.toString(), label: name };
        }),
    [staffResponse, admin.id]
  );

  const currencyLabel = (currencyId: number): string => {
    const currency = currencies.find((c) => c.id === currencyId);
    if (!currency) return `#${currencyId}`;
    return i18n.language === "he" ? currency.currencyName : currency.currencyCode;
  };

  // A successful purchase clears the picker back to its initial state, ready for another one.
  useEffect(() => {
    if (purchase.outcome?.kind !== "success") return;
    setSelectedPackage(null);
    setSelectedCurrencyId(null);
    setSelectedScope(null);
    setSelectedUserId(null);
  }, [purchase.outcome]);

  const selectPackage = (pkg: FeatureCreditPackageDto) => {
    if (selectedPackage?.id === pkg.id) return;
    setSelectedPackage(pkg);
    // Every purchasable pack carries exactly one price the company can be charged in (see
    // PackageCard), so picking a pack picks its currency — there is nothing left for the buyer
    // to choose.
    setSelectedCurrencyId(pkg.prices[0].currencyId);
    setSelectedScope(pkg.creditType === "seat" ? "company" : null);
    setSelectedUserId(null);
    setUserError(false);
    purchase.resetAttempt();
  };

  const handleScopeChange = (scope: ScopeType) => {
    setSelectedScope(scope);
    setUserError(false);
    purchase.resetAttempt();
    if (scope === "specific" && !staffResponse) {
      getStaffMembers({
        companyIds: [admin.companyId],
        roleIds: STAFF_ROLE_IDS,
        fakeCompanyIds: [-1],
      });
    }
  };

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    setUserError(false);
    purchase.resetAttempt();
  };

  const beneficiaryLabelFor = (scope: ScopeType, userId: number | null): string => {
    if (scope === "company") return t("SCOPE_WHOLE_COMPANY");
    // if (scope === "self") return admin.fullName || t("SCOPE_MYSELF"); scope self was removed (Inon decision)
    return staffMembers.find((s) => s.value === String(userId))?.label ?? t("SCOPE_SPECIFIC_STAFF_MEMBER");
  };

  const handleConfirm = () => {
    if (!selectedPackage || !selectedCurrencyId || !selectedScope) return;

    if (selectedScope === "specific" && !selectedUserId) {
      setUserError(true);
      return;
    }

    const targetUserId =
     selectedScope === "specific" ? selectedUserId! : undefined;

    setLastBeneficiaryLabel(beneficiaryLabelFor(selectedScope, selectedUserId));
    purchase.submit({
      packageId: selectedPackage.id,
      currencyId: selectedCurrencyId,
      targetUserId,
    });
  };

  // The catalog endpoint may or may not already scope results to (global ∪
  // caller's company); filter defensively so a company-scoped pack that
  // isn't the admin's own company never shows up as purchasable.
  const groups = useMemo(() => {
    const purchasable = packages.filter(
      (pkg) =>
        pkg.prices.length > 0 && (pkg.companyId == null || pkg.companyId === admin.companyId)
    );
    return CREDIT_TYPES.map((creditType) => ({
      creditType,
      packages: purchasable.filter((pkg) => pkg.creditType === creditType),
    })).filter((group) => group.packages.length > 0);
  }, [packages, admin.companyId]);

  const isConfirmDisabled =
    purchase.isBusy || !selectedPackage || !selectedCurrencyId || !selectedScope;
  const isSeatPackage = selectedPackage?.creditType === "seat";

  const eligibility = purchase.eligibility;
  const eligibilityForbidden = eligibility.isError && (eligibility.error as { status?: number })?.status === 403;

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>💳</span>
          <div className={styles.sectionTitleGroup}>
            <span className={styles.sectionTitle}>{t("BUY_CREDITS")}</span>
            <span className={styles.sectionSubtitle}>{t("BUY_CREDITS_DESC")}</span>
          </div>
        </div>
        <div className={styles.sectionBody}>
          {purchase.outcome && (
            <PurchaseOutcomeBanner
              outcome={purchase.outcome}
              beneficiaryLabel={lastBeneficiaryLabel}
              onDismiss={purchase.resetAttempt}
              onUpdatePaymentMethod={() => onNavigateToTab?.("payment")}
              onViewSeatPacks={() => onNavigateToTab?.("seat-packs")}
            />
          )}

          {eligibilityForbidden ? null : eligibility.isLoading || isLoading ? (
            <div className={styles.loading}>{t("LOADING")}</div>
          ) : eligibility.data?.canBuy === false ? (
            <EligibilityBlockedMessage reason={eligibility.data.reason} onNavigateToTab={onNavigateToTab} />
          ) : eligibility.data?.canBuy !== true ? null : isError ? (
            <p className={styles.loading}>{t("SOMETHING_WENT_WRONG_TRY_AGAIN")}</p>
          ) : groups.length === 0 ? (
            <p className={styles.loading}>{t("NO_CREDIT_PACKAGES_AVAILABLE")}</p>
          ) : (
            <div className={styles.picker}>
              {groups.map((group) => (
                <div key={group.creditType} className={styles.group}>
                  <h4 className={styles.groupTitle}>{creditTypeLabel(group.creditType)}</h4>
                  <div className={styles.cardGrid}>
                    {group.packages.map((pkg) => (
                      <PackageCard
                        key={pkg.id}
                        pkg={pkg}
                        isSelected={selectedPackage?.id === pkg.id}
                        disabled={purchase.isBusy}
                        currencyLabel={currencyLabel}
                        onSelectPackage={selectPackage}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {selectedPackage && (
                <div className={styles.checkoutSection}>
                  <ScopeSelector
                    packageCreditType={selectedPackage.creditType}
                    selectedScope={selectedScope}
                    selectedUserId={selectedUserId}
                    userError={userError}
                    staffMembers={staffMembers}
                    disabled={purchase.isBusy}
                    onScopeChange={handleScopeChange}
                    onUserChange={handleUserChange}
                  />

                  {isSeatPackage && (
                    <ExistingSeatPackNotice
                      pkg={selectedPackage}
                      seatRows={seatRows}
                      onNavigateToTab={onNavigateToTab}
                    />
                  )}

                  {selectedCurrencyId && (
                    <p className={styles.confirmCopy}>
                      {(() => {
                        const price = `${currencyLabel(selectedCurrencyId)} ${
                          selectedPackage.prices.find((p) => p.currencyId === selectedCurrencyId)
                            ?.priceAfterTax ?? ""
                        }`;
                        const credits = packageSizeLabel(selectedPackage);

                        return eligibility.data?.cardLast4
                          ? t(isSeatPackage ? "CONFIRM_CHARGE_CARD_RECURRING" : "CONFIRM_CHARGE_CARD", {
                              last4: eligibility.data.cardLast4,
                              price,
                              credits,
                            })
                          : t(isSeatPackage ? "CONFIRM_CHARGE_NO_LAST4_RECURRING" : "CONFIRM_CHARGE_NO_LAST4", {
                              price,
                              credits,
                            });
                      })()}
                    </p>
                  )}

                  <button
                    type="button"
                    className={`button ${styles.buyButton} ${isConfirmDisabled&&"button_disabled"}`}
                    disabled={isConfirmDisabled}
                    onClick={handleConfirm}
                  >
                    {purchase.isBusy ? t("PROCESSING") : t("CONFIRM_PURCHASE")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
